import React from 'react';
import { getVerticalConfig, isRestaurantOnly } from '../config/verticals';

/**
 * DataPanel — "Your Plan So Far" sidebar.
 *
 * STRICT ZERO-HALLUCINATION CONTRACT
 * ──────────────────────────────────
 * Every cell is a SINGLE direct read from either:
 *   1. `collectedInputs[fieldName].value`   — confirmed user input
 *   2. `latestPlan.<direct path>`           — engine-calculated output
 * No arithmetic, sums, or derived numbers are computed here. The Excel export
 * does the heavier math; this panel is a thin, auditable lens.
 *
 * VERTICAL-AWARE: the section/field layout and the Key Financials metrics come
 * entirely from config/verticals.js — this component has NO vertical branching.
 */

// ─── Formatters ───────────────────────────────────────────────────────
// Deterministic transforms of a SINGLE value; never combine fields.

function fmtCurrency(value) {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtPct(value) {
  // Schema/engine store percentages as 0–1 fractions (occupancy_pct=0.6,
  // utilization_rate=0.7, etc.). break_even_occupancy is the lone 0–100
  // exception, handled by fmtBigPct.
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtBigPct(value) {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function fmtInt(value) {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

function fmtBool(value) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const s = String(value).trim().toLowerCase();
  if (s === 'true' || s === 'yes' || s === '1') return 'Yes';
  if (s === 'false' || s === 'no' || s === '0' || s === '') return 'No';
  return String(value);
}

// business_stage enum → reader-friendly label.
const STAGE_LABELS = {
  pre_launch: 'Pre-launch',
  operating_lt_2yr: 'Operating (under 2 years)',
  operating_gt_2yr: 'Operating (2+ years)',
};

function formatField(fmt, value) {
  switch (fmt) {
    case 'text':                return String(value);
    case 'stage':               return STAGE_LABELS[value] || String(value);
    case 'bool':                return fmtBool(value);
    case 'int':                 return fmtInt(value);
    case 'currency':            return fmtCurrency(value);
    case 'currency_per_month':  return `${fmtCurrency(value)}/mo`;
    case 'pct':                 return fmtPct(value);
    case 'sqft':                return `${fmtInt(value)} sq ft`;
    case 'turns_per_day':       return `${value}/day`;
    case 'days_per_month':      return `${fmtInt(value)} days/mo`;
    case 'currency_or_none': {
      const n = typeof value === 'string' ? parseFloat(value) : Number(value);
      if (Number.isNaN(n) || n === 0) return 'None';
      return fmtCurrency(n);
    }
    default:                    return '—';
  }
}

// Key-Financials format hint → formatter. (Separate enum because these come
// from the engine output and use percent variants.)
function formatMetric(fmt, value) {
  switch (fmt) {
    case 'currency':    return fmtCurrency(value);
    case 'integer':     return fmtInt(value);
    case 'percent':     return fmtPct(value);
    case 'bigpercent':  return fmtBigPct(value);
    default:            return '—';
  }
}

// ─── Source-tag chip ──────────────────────────────────────────────────

const SOURCE_CHIPS = {
  rag_benchmark:  { label: 'benchmark', className: 'data-panel-source-rag' },
  system_default: { label: 'default',   className: 'data-panel-source-default' },
};

function SourceChip({ source }) {
  const cfg = SOURCE_CHIPS[source];
  if (!cfg) return null;
  return <span className={`data-panel-source ${cfg.className}`}>{cfg.label}</span>;
}

// ─── Section renderer ─────────────────────────────────────────────────

function Section({ title, fields, collectedInputs }) {
  // A section only renders once a "real" (non-alwaysShow) input has landed —
  // alwaysShow rows (e.g. "Loans: None") ride along to confirm explicit zeros.
  let hasRealRow = false;

  const rows = fields
    .map((f) => {
      const ci = collectedInputs[f.name];
      const hasValue = ci && ci.value != null && ci.value !== '';

      if (hasValue) hasRealRow = true;
      else if (!f.alwaysShow) return null;

      const value = hasValue ? ci.value : 0;
      const sourceTag = hasValue ? ci.source_tag : null;

      return (
        <div key={f.name} className="data-panel-row">
          <span className="data-panel-label">{f.label}</span>
          <span className="data-panel-value">
            {formatField(f.format, value)}
            {sourceTag && <SourceChip source={sourceTag} />}
          </span>
        </div>
      );
    })
    .filter(Boolean);

  if (!hasRealRow) return null;
  return (
    <section className="data-panel-section">
      <h3 className="data-panel-section-title">{title}</h3>
      <div className="data-panel-section-body">{rows}</div>
    </section>
  );
}

// ─── Catch-all: collected fields not in the curated config ───────────
//
// The curated dataPanelSections list had fallen behind schema growth, so
// newly-added fields (business_stage, owner salary, other_financing, …) were
// collected but silently invisible. This section renders EVERY remaining
// collected field so nothing the user answered disappears. Zero-hallucination
// discipline: raw values only — the field's unit isn't known here, so no
// $/% decoration is guessed; numbers just get locale separators.

function humanize(name) {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function fmtRaw(value) {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isNaN(n) && String(value).trim() !== '' && isFinite(n)
      && /^-?\d*\.?\d+$/.test(String(value).trim())) {
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  const s = String(value);
  return s.length > 90 ? `${s.slice(0, 90)}…` : s;
}

function AdditionalDetails({ collectedInputs, configuredNames }) {
  const rows = Object.entries(collectedInputs)
    .filter(([name, ci]) => !configuredNames.has(name)
      && ci && ci.value != null && ci.value !== '')
    .map(([name, ci]) => (
      <div key={name} className="data-panel-row">
        <span className="data-panel-label">{humanize(name)}</span>
        <span className="data-panel-value">
          {fmtRaw(ci.value)}
          <SourceChip source={ci.source_tag} />
        </span>
      </div>
    ));
  if (rows.length === 0) return null;
  return (
    <section className="data-panel-section">
      <h3 className="data-panel-section-title">Additional Details</h3>
      <div className="data-panel-section-body">{rows}</div>
    </section>
  );
}

// ─── Key Financials (engine-calculated) ───────────────────────────────
//
// Common rows (vertical-agnostic) + the vertical's keyMetrics from config.
// Each row is ONE direct read — no reductions or joins.

function KeyFinancials({ latestPlan, collectedInputs, vertical }) {
  if (!latestPlan) return null;

  const sc = latestPlan.startup_costs;
  const be = latestPlan.break_even;
  const baseCase = (latestPlan.scenarios?.scenarios || []).find(
    (s) => s.scenario_name === 'base_case'
  );
  const month12 = latestPlan.pnl?.months?.[11];

  // Vertical-varying break-even / utilization / volume metrics from config.
  const metricRows = getVerticalConfig(vertical).keyMetrics
    .filter((m) => !(isRestaurantOnly(m.key) && vertical !== 'restaurant'))
    .map((m) => {
      let raw;
      if (m.source === 'breakeven') raw = be ? be[m.key] : undefined;
      else if (m.source === 'collected') raw = collectedInputs[m.key]?.value;
      if (raw == null) return null;
      return { label: m.label, value: formatMetric(m.format, raw) };
    })
    .filter(Boolean);

  const commonRows = [
    month12 && {
      label: 'Monthly Revenue (Mo. 12)',
      value: fmtCurrency(month12.revenue),
    },
    baseCase && {
      label: 'First Profitable Month',
      value: baseCase.first_profitable_month
        ? `Month ${baseCase.first_profitable_month}`
        : 'Not yet profitable',
    },
    sc && {
      label: 'Total Capital Required',
      value: fmtCurrency(sc.total_capital_required),
    },
  ].filter(Boolean);

  const rows = [...metricRows, ...commonRows];
  if (rows.length === 0) return null;

  return (
    <section className="data-panel-section data-panel-section-engine">
      <h3 className="data-panel-section-title">
        Key Financials
        <span className="data-panel-source-engine">calculated by engine</span>
      </h3>
      <div className="data-panel-section-body">
        {rows.map((r) => (
          <div key={r.label} className="data-panel-row">
            <span className="data-panel-label">{r.label}</span>
            <span className="data-panel-value">{r.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Main component ───────────────────────────────────────────────────

export default function DataPanel({
  collectedInputs = {},
  latestPlan = null,
  vertical = 'restaurant',
  open = true,
  onClose = () => {},
  embedded = false,
}) {
  const hasAnyInputs = Object.keys(collectedInputs).length > 0;
  const sections = getVerticalConfig(vertical).dataPanelSections;

  const body = (
      <div className="data-panel-body">
        {!hasAnyInputs && (
          <p className="data-panel-empty">
            As you answer Eliza's questions, your inputs will appear here.
          </p>
        )}

        {sections.map((section) => (
          <Section
            key={section.section}
            title={section.section}
            fields={section.fields}
            collectedInputs={collectedInputs}
          />
        ))}

        <AdditionalDetails
          collectedInputs={collectedInputs}
          configuredNames={new Set(
            sections.flatMap((s) => s.fields.map((f) => f.name))
          )}
        />

        {!latestPlan && hasAnyInputs && (
          <p className="data-panel-empty data-panel-empty-bottom">
            Your financial calculations will appear here once all
            details are collected.
          </p>
        )}

        <KeyFinancials
          latestPlan={latestPlan}
          collectedInputs={collectedInputs}
          vertical={vertical}
        />
      </div>
  );

  // When `embedded` (inside RightPanelTabs), render just the body — the tab
  // wrapper supplies the <aside> + header.
  if (embedded) return body;

  return (
    <aside
      className={`data-panel ${open ? 'data-panel-open' : 'data-panel-closed'}`}
      aria-label="Your Plan So Far"
    >
      <header className="data-panel-header">
        <h2>Your Plan So Far</h2>
        <button
          type="button"
          className="data-panel-close-btn"
          aria-label="Close panel"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      {body}
    </aside>
  );
}
